/**
 * Get item from local storage given key
 * returns parsed JSON object
 *
 * @param key - key to fetch
 * @param parseJSON - if TRUE will use JSON.parse() to parse local storage item, will return the parsed object
 * @returns object - parsed local storage item
 */
const getLocalStorageItem = (key: string, parseJSON = true) => {
  if (!window.localStorage) return null;

  let obj: object | null = null;
  try {
    const item = window.localStorage.getItem(key);
    if (item) {
      obj = parseJSON ? JSON.parse(item) : item;
    }
  } catch (err: any) {
    console.error(`Unable to fetch local storage item: ${key}`, err.message);
  }

  return obj;
};

/**
 * Set item in local storage
 * Overwrites the specified key
 *
 * @param key: string key to use
 * @param obj: object to store
 * @returns boolean - TRUE if local storage item successfully set
 */
const setLocalStorageItem = (key: string, obj: object) => {
  if (!window.localStorage) return false;

  try {
    window.localStorage.setItem(key, JSON.stringify(obj));
  } catch (err: any) {
    console.error(`Unable to set local storage item: ${key}`, err.message);
    return false;
  }

  return true;
};

/**
 * Removes item from local storage
 *
 * @param key - item to remove
 * @returns boolean - TRUE if item was successfully removed
 */
const clearLocalStorageItem = (key: string) => {
  if (!window.localStorage) return false;

  try {
    window.localStorage.removeItem(key);
  } catch (err: any) {
    console.error(`Unable to clear local storage item: ${key}`, err.message);
    return false;
  }

  return true;
};

export { getLocalStorageItem, setLocalStorageItem, clearLocalStorageItem };
